import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { SITE } from "@/constants/testIds";

export default function BlogPage() {
    const [posts, setPosts] = useState([]);
    const [query, setQuery] = useState("");
    const [cat, setCat] = useState("All");

    useEffect(() => {
        api.posts(50).then(setPosts).catch(() => {});
    }, []);

    const categories = useMemo(() => {
        const set = new Set(["All"]);
        posts.forEach((p) => set.add(p.category));
        return Array.from(set);
    }, [posts]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return posts.filter((p) => {
            const inCat = cat === "All" || p.category === cat;
            if (!inCat) return false;
            if (!q) return true;
            return (
                p.title.toLowerCase().includes(q) ||
                p.excerpt.toLowerCase().includes(q) ||
                (p.tags || []).some((t) => t.toLowerCase().includes(q))
            );
        });
    }, [posts, query, cat]);

    const featured = filtered[0];
    const rest = filtered.slice(1);

    return (
        <div>
            <PageHeader
                eyebrow="STORIES & NEWS"
                title="From the journal"
                subtitle="Reflections on Ayurveda, healing, and the daily practices that quietly change everything."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Blog" }]}
            >
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative w-full sm:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50" />
                        <input
                            data-testid={SITE.blogSearchInput}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search articles..."
                            className="w-full rounded-xl bg-white/90 border border-border/80 pl-9 pr-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setCat(c)}
                                data-testid={`blog-cat-${c}`}
                                className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                                    cat === c
                                        ? "bg-moss text-cream border-moss"
                                        : "bg-white/80 text-foreground/75 border-border/80 hover:border-moss/40"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </PageHeader>

            <section className="py-10 sm:py-12">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    {filtered.length === 0 && (
                        <p className="text-foreground/70">No articles match your search yet.</p>
                    )}

                    {featured && (
                        <Link
                            to={`/blog/${featured.slug}`}
                            data-testid={SITE.blogCard}
                            className="group block rounded-3xl overflow-hidden border border-border/80 bg-white/85 hover:-translate-y-0.5 transition-transform"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2">
                                <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden bg-sand">
                                    <img
                                        src={featured.cover_image}
                                        alt={featured.title}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-6 sm:p-8 flex flex-col justify-center">
                                    <div className="flex items-center gap-2 text-xs text-foreground/65">
                                        <span className="rounded-full bg-sand px-2 py-0.5 text-moss">
                                            Featured
                                        </span>
                                        <span>·</span>
                                        <span>{featured.category}</span>
                                        <span>·</span>
                                        <span>{featured.read_time}</span>
                                    </div>
                                    <h2
                                        data-testid={SITE.blogPostTitle}
                                        className="mt-3 font-display text-3xl sm:text-4xl text-moss leading-tight"
                                    >
                                        {featured.title}
                                    </h2>
                                    <p className="mt-3 text-foreground/75 leading-relaxed">
                                        {featured.excerpt}
                                    </p>
                                    <span
                                        data-testid={SITE.blogReadMore}
                                        className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-moss group-hover:text-terracotta"
                                    >
                                        Read article →
                                    </span>
                                </div>
                            </div>
                        </Link>
                    )}

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {rest.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/blog/${p.slug}`}
                                data-testid={SITE.blogCard}
                                className="group rounded-2xl overflow-hidden border border-border/80 bg-white/85 hover:-translate-y-0.5 transition-transform"
                            >
                                <div className="relative aspect-[16/10] overflow-hidden bg-sand">
                                    <img
                                        src={p.cover_image}
                                        alt={p.title}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-2 text-xs text-foreground/60">
                                        <span className="rounded-full bg-sand px-2 py-0.5 text-moss">
                                            {p.category}
                                        </span>
                                        <span>·</span>
                                        <span>{p.read_time}</span>
                                    </div>
                                    <h3
                                        data-testid={SITE.blogPostTitle}
                                        className="mt-2 font-display text-lg text-moss leading-snug"
                                    >
                                        {p.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-foreground/70 line-clamp-3">
                                        {p.excerpt}
                                    </p>
                                    <span
                                        data-testid={SITE.blogReadMore}
                                        className="mt-3 inline-flex text-sm text-moss group-hover:text-terracotta"
                                    >
                                        Read more →
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
